import slugify from '@sindresorhus/slugify';
import { buildCardBadgesFromFacilities } from './property-card-badges.js';

function slugifyName(name) {
  return slugify(name, {
    separator: '-',
    lowercase: true,
    decamelize: false,
  }).replace(/^-+|-+$/g, '');
}

function parseSlug(slug) {
  const [urlFriendlyCity = '', urlFriendlyCountry = ''] = String(slug || '').split('/');

  return {
    urlFriendlyCity,
    urlFriendlyCountry,
  };
}

function buildImage(propertyImage) {
  if (!propertyImage) {
    return null;
  }

  return {
    small: propertyImage,
    medium: propertyImage,
    large: propertyImage,
  };
}

function updateApiCityProperties(propertyList) {
  return propertyList?.map((property) => {
    const whitelabelUrl = property._whitelabelUrl;

    property.description = property.intro;
    property.rating = property.avgRating;
    property.numReviews = property.numberReviews;
    property.distance = property.cityCenterDistance;

    property.minDormPrice = property.sharedMinPrice;
    property.minPrivatePrice = property.privateMinPrice;
    property.urlPath = whitelabelUrl;
    property.imgSizes = property.imageSizes?.propertyList;
    property.latitude = property.geoCoordinates?.latitude || null;
    property.longitude = property.geoCoordinates?.longitude || null;

    delete property._whitelabelUrl;

    return property;
  });
}

function map3piProperty(sourceProperty, cityContext) {
  const {
    city,
    country,
    urlFriendlyCity,
    urlFriendlyCountry,
    currency,
  } = cityContext;
  const image = buildImage(sourceProperty.propertyImage);
  const priceValue = sourceProperty.perNightPrice ?? sourceProperty.lowestPrice;
  const price = priceValue != null
    ? {
      value: priceValue,
      currency,
    }
    : undefined;

  const property = {
    id: Number.parseInt(sourceProperty.id, 10),
    // COD-877: provider (booking.com) id and raw category/labels are needed by the 3PI tracking events.
    providerId: sourceProperty.providerId || null,
    propertyCategory: sourceProperty.propertyType || '',
    isNew: !!sourceProperty.isNew,
    hasFreeCancellation: !!sourceProperty.isFreeCancellationAvailable,
    name: sourceProperty.name,
    // COD-989: all Zentrum inventory is type 3PI (TPI URL, no card prices); propertyCategory drives the pill.
    type: '3PI',
    urlFriendlyName: slugifyName(sourceProperty.name),
    city,
    urlFriendlyCity,
    country,
    urlFriendlyCountry,
    avgRating: sourceProperty.displayableRating ? Number.parseFloat(sourceProperty.displayableRating) : 0,
    numberReviews: sourceProperty.numberOfReviews ?? 0,
    cityCenterDistance: sourceProperty.distanceValueAndUnits
      ? Number.parseFloat(sourceProperty.distanceValueAndUnits)
      : 0,
    hasAvailability: true,
    intro: '',
    shortIntro: null,
    tracking: {
      name: sourceProperty.name,
      city,
      country,
    },
    geoCoordinates: {
      latitude: Number.parseFloat(sourceProperty.latitude),
      longitude: Number.parseFloat(sourceProperty.longitude),
    },
    badges: buildCardBadgesFromFacilities(sourceProperty.facilities),
    facilities: sourceProperty.facilities || [],
    starRating: sourceProperty.starRating ?? 0,
    hasRating: !!sourceProperty.hasRating,
    // Catalog uses whitelabelURL; JSON dumps / older payloads use whitelabelUrl.
    _whitelabelUrl: sourceProperty.whitelabelURL || sourceProperty.whitelabelUrl,
  };

  if (image) {
    property.image = image;
    property.images = [image];
    property.totalImagesCount = 1;
  }

  if (price) {
    // Stored for the 3PI q token (propertypricefrom) only — cards never show prices for type 3PI.
    property.privateMinPrice = price;
  }

  return property;
}

export function map3piSourceFile(sourceFile) {
  const {
    urlFriendlyCity,
    urlFriendlyCountry,
  } = parseSlug(sourceFile.slug);
  const currency = sourceFile.apiMeta?.currency || 'EUR';
  const cityContext = {
    city: sourceFile.city,
    country: sourceFile.country,
    urlFriendlyCity,
    urlFriendlyCountry,
    currency,
  };

  const rawProperties = (sourceFile.properties || []).map((sourceProperty) =>
    map3piProperty(sourceProperty, cityContext),
  );
  const properties = updateApiCityProperties(rawProperties);

  const totalFromApi = Number.parseInt(sourceFile.apiMeta?.total ?? sourceFile.returnedCount, 10);

  return {
    city: sourceFile.city,
    country: sourceFile.country,
    slug: sourceFile.slug,
    totalPropertiesCount: Number.isFinite(totalFromApi) ? totalFromApi : properties.length,
    // COD-877: properties available in the location before availability/filters (third_party_properties_estimated).
    estimatedPropertiesCount: Number.parseInt(sourceFile.apiMeta?.expectedCount, 10) || properties.length,
    numberOfPages: 1,
    page: 1,
    properties,
  };
}
